import React from 'react';

import realContactSmall from '@assets/img/icons/real-contact-small.svg';
import { contactStrings } from '@data/contacts';
import { ContactForm } from '@models/Contact';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import clsx from 'clsx';
import Color from 'color';

const strings = createStrings({
  ja: {
    space: '　',
    realContactTip: 'D2D MEMBERS連携済み',
    fakePhone: '12345678910',
    fakeAddress2: 'D2D MEMBERSの住所2',
    fakeSnsId: 'D2D MEMBERSのSNS ID',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  contactDisplayContainer: {
    ...common.flexColumn,
    ...common.middleText,
    flex: 1,
    minWidth: 0,
    rowGap: '0.25rem',
  },
  contactDisplayWrapper: {
    ...common.flexInline,
    columnGap: 1,
  },
  contactDisplayIndicator: {
    backgroundColor: theme.palette.grey[100],
    width: 12,
  },
  contactListContainer: {
    ...common.flexColumn,
    overflow: 'auto',
    height: 360,
    rowGap: 10,
    [theme.breakpoints.down('xs')]: {
      height: '23vh',
    },
  },
  contactDisplayRow: {
    ...common.flexInline,
    [theme.breakpoints.down('xs')]: {
      ...common.flexColumn,
    },
    '$stale &': {
      textDecoration: 'line-through',
    },
  },
  contactDisplayColumn: {
    width: '50%',
    '$stale &': {
      textDecoration: 'line-through',
    },
  },
  textInline: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    wordWrap: 'normal',
    flexWrap: 'nowrap',
  },
  contactFieldEmpty: {
    color: theme.palette.grey[500],
  },
  contactHead: {
    marginBottom: '0.2rem',
    ...common.fontWeight600Important,
    '$big &': {
      ...common.bigText,
    },
  },
  realContact: {
    position: 'relative',
    top: 2,
    marginLeft: '1rem',
  },
  realContactTip: {
    ...common.textGray700,
    fontSize: 10,
    lineHeight: 1,
    pointerEvents: 'none',
    fontWeight: 100,
    position: 'absolute',
    left: '160%',
    top: -12,
    backgroundColor: Color(theme.palette.primary.main).alpha(0.1).toString(),
    padding: '14px 9px',
    whiteSpace: 'pre',
    zIndex: 1,
    boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
    opacity: 0,
    '$realContact:hover &': {
      opacity: 1,
    },
  },
  desensitized: {
    color: 'transparent',
    textShadow: '0 0 10px rgba(0,0,0,0.5)',
    userSelect: 'none',
  },
  companyName: {},
  stale: {},
  big: {},
}));

export interface RealContactTipProps {}

const RealContactTip: React.FC<RealContactTipProps> = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return <div className={classes.realContactTip}>{strings.realContactTip}</div>;
};

export interface ContactDisplayProps {
  isRealUser?: boolean;
  contact: Omit<ContactForm, 'user'>;
  fieldTextWrap?: boolean;
  stale?: boolean;
  desensitized?: boolean;
  variant?: 'big' | 'default';
  showAddress1CN?: boolean;
  showSocialCreditId?: boolean;
}

export const ContactDisplay: React.FC<ContactDisplayProps> = React.memo((props) => {
  const {
    isRealUser,
    contact,
    fieldTextWrap,
    stale,
    desensitized,
    showAddress1CN,
    showSocialCreditId,
    variant = 'default',
  } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div
      role="ContactDisplay"
      className={clsx(
        classes.contactDisplayContainer,
        stale && classes.stale,
        variant === 'big' && classes.big,
      )}
    >
      <div className={clsx(classes.contactDisplayRow, classes.contactHead, classes.companyName)}>
        <span
          className={clsx(
            classes.textBlack,
            !contact.company_name && classes.textGray500Important,
            classes.preEllipsis,
          )}
        >
          {contact.company_name || contactStrings.company_name}
        </span>
        <span
          className={clsx(
            classes.fontWeight400Important,
            !contact.english_name && classes.textGray500Important,
            classes.preEllipsis,
          )}
        >
          {' - '}
          {contact.english_name || contactStrings.english_name}
        </span>
        {isRealUser && (
          <span className={classes.realContact}>
            <img alt="Real Contact" src={realContactSmall} style={{ display: 'block' }} />
            <RealContactTip />
          </span>
        )}
      </div>

      {showSocialCreditId && (
        <div className={classes.contactDisplayRow}>
          <span
            className={clsx(
              classes.contactDisplayColumn,
              !contact.social_credit_id && classes.contactFieldEmpty,
              !fieldTextWrap && classes.textInline,
            )}
          >
            {contact.social_credit_id || `${contactStrings.social_credit_id}...`}
          </span>
        </div>
      )}

      <div className={classes.contactDisplayRow}>
        <div
          className={clsx(
            classes.contactDisplayColumn,
            !contact.zip_code && classes.contactFieldEmpty,
            !fieldTextWrap && classes.textInline,
          )}
        >
          {contact.zip_code || `${contactStrings.zip_code}...`}
        </div>
        <div className={classes.contactDisplayColumn}>
          {contact.principal_name}
          {strings.space}
          {desensitized ? (
            <span className={classes.desensitized}>{strings.fakePhone}</span>
          ) : (
            contact.phone
          )}
        </div>
      </div>

      <div className={classes.contactDisplayRow}>
        <div
          className={clsx(
            classes.contactDisplayColumn,
            !contact.address1 && classes.contactFieldEmpty,
            !fieldTextWrap && classes.textInline,
          )}
        >
          {showAddress1CN && (
            <>
              {contact.address1_cn} <span className={classes.textGray400}>{contact.address1}</span>
            </>
          )}
          {!showAddress1CN && contact.address1}
        </div>
        <div
          className={clsx(
            classes.contactDisplayColumn,
            !contact.email && classes.contactFieldEmpty,
            !fieldTextWrap && classes.textInline,
          )}
        >
          {contact.email || `${contactStrings.email}...`}
        </div>
      </div>

      <div className={classes.contactDisplayRow}>
        <div
          className={clsx(
            classes.contactDisplayColumn,
            !contact.address2 && classes.contactFieldEmpty,
            !fieldTextWrap && classes.textInline,
            desensitized && classes.desensitized,
          )}
        >
          {desensitized
            ? strings.fakeAddress2
            : contact.address2 || `${contactStrings.address2}...`}
        </div>
        <div
          className={clsx(
            classes.contactDisplayColumn,
            !contact.sns_id && classes.contactFieldEmpty,
            !fieldTextWrap && classes.textInline,
            desensitized && classes.desensitized,
          )}
        >
          {desensitized ? strings.fakeSnsId : contact.sns_id || `${contactStrings.sns_id}...`}
        </div>
      </div>
    </div>
  );
});

ContactDisplay.displayName = 'ContactDisplay';
